// TextToSpeech.jsx
import React, { useState, useEffect, useRef } from "react";
import { Card, Input, Select, Spin, Popconfirm, Switch } from "antd";
import { toast } from "react-hot-toast";
import { useTranslation } from "react-i18next";
import api from "../interceptors/api";
import StandardButton from '../components/StandardButton';
import ProtectedToolWrapper from '../components/ProtectedToolWrapper';

// Every Chirp3-HD locale exposes the same 30 voice names.
// https://docs.cloud.google.com/text-to-speech/docs/list-voices-and-types
const CHIRP3_HD_NAMES = [
  "Achernar", "Achird", "Algenib", "Algieba", "Alnilam", "Aoede", "Autonoe",
  "Callirrhoe", "Charon", "Despina", "Enceladus", "Erinome", "Fenrir", "Gacrux",
  "Iapetus", "Kore", "Laomedeia", "Leda", "Orus", "Puck", "Pulcherrima",
  "Rasalgethi", "Sadachbia", "Sadaltager", "Schedar", "Sulafat", "Umbriel",
  "Vindemiatrix", "Zephyr", "Zubenelgenubi",
];

// "English (US)" + "Studio O" -> "English (US - Studio O)"
const withVariant = (label, variant) =>
  label.endsWith(")") ? label.replace(/\)$/, ` - ${variant})`) : `${label} (${variant})`;

const chirp3Voices = (label, code) =>
  CHIRP3_HD_NAMES.map((n) => ({
    name: withVariant(label, `Chirp3 HD ${n}`),
    code,
    voice: `${code}-Chirp3-HD-${n}`,
  }));

const studioVoice = (label, code, letter) => ({
  name: withVariant(label, `Studio ${letter}`),
  code,
  voice: `${code}-Studio-${letter}`,
});

const LANGUAGES = [
  { name: "English (India)", code: "en-IN", voice: "en-IN-Neural2-B" },
  ...chirp3Voices("English (India)", "en-IN"),
  { name: "English (US)", code: "en-US", voice: "en-US-Neural2-J" },
  studioVoice("English (US)", "en-US", "O"),
  studioVoice("English (US)", "en-US", "Q"),
  ...chirp3Voices("English (US)", "en-US"),
  { name: "English (UK)", code: "en-GB", voice: "en-GB-Neural2-D" },
  studioVoice("English (UK)", "en-GB", "B"),
  studioVoice("English (UK)", "en-GB", "C"),
  ...chirp3Voices("English (UK)", "en-GB"),
  { name: "Spanish (Spain)", code: "es-ES", voice: "es-ES-Neural2-F" },
  studioVoice("Spanish (Spain)", "es-ES", "C"),
  studioVoice("Spanish (Spain)", "es-ES", "F"),
  // Google has no es-MX voices; es-US is its Latin-American Spanish locale.
  { name: "Spanish (US)", code: "es-US", voice: "es-US-Neural2-B" },
  studioVoice("Spanish (US)", "es-US", "B"),
  { name: "French (France)", code: "fr-FR", voice: "fr-FR-Neural2-G" },
  studioVoice("French (France)", "fr-FR", "A"),
  studioVoice("French (France)", "fr-FR", "D"),
  { name: "French (Canada)", code: "fr-CA", voice: "fr-CA-Neural2-C" },
  { name: "German (Germany)", code: "de-DE", voice: "de-DE-Neural2-H" },
  studioVoice("German (Germany)", "de-DE", "B"),
  studioVoice("German (Germany)", "de-DE", "C"),
  { name: "Italian (Italy)", code: "it-IT", voice: "it-IT-Neural2-F" },
  { name: "Portuguese (Brazil)", code: "pt-BR", voice: "pt-BR-Neural2-B" },
  { name: "Portuguese (Portugal)", code: "pt-PT", voice: "pt-PT-Wavenet-F" },
  { name: "Hindi (India)", code: "hi-IN", voice: "hi-IN-Neural2-A" },
  ...chirp3Voices("Hindi (India)", "hi-IN"),
  { name: "Japanese", code: "ja-JP", voice: "ja-JP-Neural2-C" },
  { name: "Korean", code: "ko-KR", voice: "ko-KR-Neural2-B" },
  { name: "Chinese (Mandarin, China)", code: "cmn-CN", voice: "cmn-CN-Wavenet-A" },
  { name: "Chinese (Mandarin, Taiwan)", code: "cmn-TW", voice: "cmn-TW-Wavenet-A" },
  // Google has no ar-EG voices; ar-XA is its pan-Arabic locale.
  { name: "Arabic", code: "ar-XA", voice: "ar-XA-Wavenet-A" },
  { name: "Russian", code: "ru-RU", voice: "ru-RU-Wavenet-B" },
];

// Persist recent TTS renders in localStorage as base64 data URLs so audio
// survives reloads. Cap total stored bytes to stay well under the ~5MB
// per-origin localStorage budget — oldest entries evict first.
const HISTORY_KEY = "quto.ttsHistory.v1";
const HISTORY_MAX_ENTRIES = 15;
const HISTORY_MAX_BYTES = 4 * 1024 * 1024;

const blobToDataUrl = (blob) =>
  new Promise((resolve, reject) => {
    const reader = new FileReader();
    reader.onload = () => resolve(reader.result);
    reader.onerror = reject;
    reader.readAsDataURL(blob);
  });

const readHistory = () => {
  try {
    const raw = localStorage.getItem(HISTORY_KEY);
    if (!raw) return [];
    const parsed = JSON.parse(raw);
    return Array.isArray(parsed) ? parsed : [];
  } catch {
    return [];
  }
};

const trimHistory = (entries) => {
  const trimmed = entries.slice(0, HISTORY_MAX_ENTRIES);
  let total = 0;
  const kept = [];
  for (const e of trimmed) {
    const size = (e.audioDataUrl && e.audioDataUrl.length) || 0;
    if (total + size > HISTORY_MAX_BYTES && kept.length > 0) break;
    total += size;
    kept.push(e);
  }
  return kept;
};

const writeHistory = (entries) => {
  try {
    localStorage.setItem(HISTORY_KEY, JSON.stringify(entries));
    return true;
  } catch {
    // Quota exceeded — drop the oldest and try once more.
    if (entries.length > 1) {
      try {
        localStorage.setItem(HISTORY_KEY, JSON.stringify(entries.slice(0, -1)));
      } catch {}
    }
    return false;
  }
};

const SSML_STARTER = `<speak>Welcome to <emphasis level="strong">Quto</emphasis>.<break time="500ms"/> This tool converts your text into <prosody rate="slow">clear, natural-sounding speech</prosody>.</speak>`;

const SSML_SNIPPETS = [
  { label: "pause 500ms", snippet: '<break time="500ms"/>' },
  { label: "pause 1s", snippet: '<break time="1s"/>' },
  { label: "emphasis", snippet: '<emphasis level="strong">word</emphasis>', studio: false },
  {
    label: "prosody",
    snippet: '<prosody rate="slow" pitch="+2st">phrase</prosody>',
    // Studio voices reject the pitch attribute.
    studioSnippet: '<prosody rate="slow">phrase</prosody>',
  },
  { label: "say-as digits", snippet: '<say-as interpret-as="digits">12345</say-as>' },
];

// Google voice tiers differ in SSML support (mirrors the backend check):
//   studio   → SSML minus <mark>, <emphasis>, <lang>, <prosody pitch>
//   chirp3   → full SSML (verified Sep 2026; <break> is the way to add pauses)
//   standard → full SSML (Neural2 / Wavenet / Standard)
const getVoiceTier = (voice = "") => {
  if (/Chirp3-HD/i.test(voice)) return "chirp3";
  if (/-Studio-/i.test(voice)) return "studio";
  return "standard";
};

const findStudioUnsupportedTags = (ssml = "") => {
  const found = [];
  if (/<emphasis[\s>]/i.test(ssml)) found.push("<emphasis>");
  if (/<mark[\s>\/]/i.test(ssml)) found.push("<mark>");
  if (/<lang[\s>]/i.test(ssml)) found.push("<lang>");
  if (/<prosody\b[^>]*\bpitch\s*=/i.test(ssml)) found.push("<prosody pitch>");
  return found;
};

const snippetsForTier = (tier) =>
  SSML_SNIPPETS.filter((s) => tier !== "studio" || s.studio !== false).map((s) => ({
    label: s.label,
    snippet: tier === "studio" && s.studioSnippet ? s.studioSnippet : s.snippet,
  }));

const TextToSpeech = () => {
  const [text, setText] = useState("Welcome to Quto! This tool converts your text into clear, natural-sounding speech.");
  const [ssml, setSsml] = useState(SSML_STARTER);
  const [ssmlMode, setSsmlMode] = useState(false);
  const [selectedVoice, setSelectedVoice] = useState("");
  const [audioUrl, setAudioUrl] = useState(null);
  const [loading, setLoading] = useState(false);
  const { t } = useTranslation();
  const [isInitialized, setIsInitialized] = useState(false);
  const [history, setHistory] = useState([]);
  const activeAudioUrlRef = useRef(null);
  const ssmlTextAreaRef = useRef(null);

  const voiceTier = getVoiceTier(selectedVoice);
  const studioUnsupportedTags =
    ssmlMode && voiceTier === "studio" ? findStudioUnsupportedTags(ssml) : [];

  const insertSsmlSnippet = (snippet) => {
    const el = ssmlTextAreaRef.current?.resizableTextArea?.textArea;
    if (!el) {
      setSsml((prev) => prev + snippet);
      return;
    }
    const start = el.selectionStart ?? ssml.length;
    const end = el.selectionEnd ?? ssml.length;
    const next = ssml.slice(0, start) + snippet + ssml.slice(end);
    setSsml(next);
    // Restore cursor after React re-renders.
    requestAnimationFrame(() => {
      el.focus();
      const pos = start + snippet.length;
      el.setSelectionRange(pos, pos);
    });
  };

  // Initialize selected voice + hydrate history after component mounts
  useEffect(() => {
    if (LANGUAGES.length > 0 && LANGUAGES[0]) {
      setSelectedVoice(LANGUAGES[0].voice);
      setIsInitialized(true);
    }
    setHistory(readHistory());
  }, []);

  // Revoke object URLs when they change so tab memory doesn't grow forever.
  useEffect(() => {
    return () => {
      if (activeAudioUrlRef.current) {
        URL.revokeObjectURL(activeAudioUrlRef.current);
      }
    };
  }, []);

  const setActiveAudioUrl = (url) => {
    if (activeAudioUrlRef.current && activeAudioUrlRef.current !== url) {
      URL.revokeObjectURL(activeAudioUrlRef.current);
    }
    activeAudioUrlRef.current = url;
    setAudioUrl(url);
  };

  const persistToHistory = async (blob, entryText, lang, isSsml = false) => {
    try {
      const audioDataUrl = await blobToDataUrl(blob);
      const entry = {
        id: `${Date.now()}-${Math.random().toString(36).slice(2, 8)}`,
        text: entryText,
        ssml: isSsml,
        voiceLabel: lang.name,
        voiceCode: lang.voice,
        languageCode: lang.code,
        audioDataUrl,
        createdAt: new Date().toISOString(),
      };
      setHistory((prev) => {
        const next = trimHistory([entry, ...prev]);
        writeHistory(next);
        return next;
      });
    } catch {
      // Persisting is best-effort — playback still works via the object URL.
    }
  };

  const handleConvert = async () => {
    const activePayload = ssmlMode ? ssml.trim() : text.trim();

    if (!activePayload) {
      return toast.error(t('please_enter_text_to_convert'));
    }

    if (ssmlMode && !/^<speak(\s|>)/i.test(activePayload)) {
      return toast.error(t('ssml_must_be_wrapped'));
    }

    if (!selectedVoice) {
      return toast.error(t('please_select_a_voice'));
    }

    const selectedLang = LANGUAGES.find((lang) => lang.voice === selectedVoice);

    if (!selectedLang) {
      return toast.error(t('invalid_voice_selected'));
    }

    if (ssmlMode && getVoiceTier(selectedLang.voice) === "studio") {
      const unsupported = findStudioUnsupportedTags(activePayload);
      if (unsupported.length) {
        return toast.error(t('ssml_unsupported_on_studio', { tags: unsupported.join(', ') }));
      }
    }

    setLoading(true);
    setActiveAudioUrl(null);
    const toastId = toast.loading(t('generating_audio'));

    try {
      const response = await api.post(
        "/text-to-speech",
        {
          ...(ssmlMode ? { ssml: activePayload } : { text: activePayload }),
          languageCode: selectedLang.code,
          languageName: selectedLang.voice,
          audioFormat: "mp3",
        },
        { responseType: "blob" }
      );

      const blob = response.data;
      const url = URL.createObjectURL(blob);
      setActiveAudioUrl(url);
      toast.dismiss(toastId);
      toast.success(t('audio_generated_success'));
      persistToHistory(blob, activePayload, selectedLang, ssmlMode);
    } catch (err) {
      toast.dismiss(toastId);
      // With responseType "blob" the JSON error body arrives as a Blob.
      let serverError = "";
      const data = err?.response?.data;
      if (data) {
        try {
          const raw = typeof data === "string" ? data : await data.text();
          serverError = JSON.parse(raw)?.error || "";
        } catch {
          // Non-JSON body — fall through to the generic message.
        }
      }
      if (serverError.includes("Insufficient credits")) {
        toast.error(t('insufficient_credits_error'));
      } else if (serverError) {
        toast.error(t('audio_generate_failed') + ' ' + serverError);
      } else {
        toast.error(t('audio_generate_failed'));
      }
    } finally {
      setLoading(false);
      toast.dismiss(toastId);
    }
  };

  const handleReuse = (entry) => {
    if (entry.ssml) {
      setSsml(entry.text);
      setSsmlMode(true);
    } else {
      setText(entry.text);
      setSsmlMode(false);
    }
    if (entry.voiceCode) setSelectedVoice(entry.voiceCode);
    setActiveAudioUrl(entry.audioDataUrl);
  };

  const handleDeleteEntry = (id) => {
    setHistory((prev) => {
      const next = prev.filter((e) => e.id !== id);
      writeHistory(next);
      return next;
    });
    toast.success(t('tts_history_deleted'));
  };

  const handleClearHistory = () => {
    localStorage.removeItem(HISTORY_KEY);
    setHistory([]);
    toast.success(t('tts_history_cleared'));
  };

  // Show loading while initializing
  if (!isInitialized) {
    return (
      <div className="min-h-screen bg-gray-50 p-0">
        <div className="max-w-5xl mx-auto px-4 pt-20">
          <div className="flex justify-center items-center h-64">
            <Spin tip="Initializing..." />
          </div>
        </div>
      </div>
    );
  }

  return (
    <ProtectedToolWrapper>
      <div className="max-w-5xl mx-auto">
        <Card className="p-6 rounded-xl shadow-lg space-y-6">
          <div className="flex items-center justify-between mb-2">
            <label className="flex items-center gap-2 text-sm text-gray-700 select-none">
              <Switch size="small" checked={ssmlMode} onChange={setSsmlMode} />
              {t('ssml_mode_label')}
            </label>
            {ssmlMode && (
              <div className="flex flex-wrap gap-1">
                {snippetsForTier(voiceTier).map((s) => (
                  <button
                    key={s.label}
                    type="button"
                    onClick={() => insertSsmlSnippet(s.snippet)}
                    className="text-xs px-2 py-1 border border-gray-300 rounded hover:bg-gray-100"
                  >
                    {s.label}
                  </button>
                ))}
              </div>
            )}
          </div>
          {ssmlMode ? (
            <>
              <Input.TextArea
                ref={ssmlTextAreaRef}
                rows={8}
                value={ssml}
                onChange={(e) => setSsml(e.target.value)}
                placeholder={t('enter_ssml_placeholder')}
                className="font-mono text-sm"
              />
              {studioUnsupportedTags.length > 0 && (
                <p className="text-xs text-amber-600 mt-2 mb-0">
                  {t('ssml_unsupported_on_studio', { tags: studioUnsupportedTags.join(', ') })}
                </p>
              )}
            </>
          ) : (
            <Input.TextArea
              rows={6}
              value={text}
              onChange={(e) => setText(e.target.value)}
              placeholder={t('enter_text_to_speech_placeholder')}
            />
          )}
          <div className="h-4" />
          <div className="flex flex-col md:flex-row gap-4">
            <Select
              className="w-full md:w-1/2"
              value={selectedVoice}
              onChange={(val) => setSelectedVoice(val)}
              placeholder={t('select_voice_placeholder')}
              showSearch
              optionFilterProp="children"
            >
              {LANGUAGES.map((lang) => (
                <Select.Option key={lang.voice} value={lang.voice}>
                  {lang.name}
                </Select.Option>
              ))}
            </Select>

            <StandardButton
              onClick={handleConvert}
              loading={loading}
              type="secondary"
              className="w-full md:w-auto"
            >
              {t('convert_button')}
            </StandardButton>
          </div>

          {audioUrl && (
            <div className="mt-6 space-y-3">
              <audio controls src={audioUrl} className="w-full" />
              <div className="h-4" />
              <a href={audioUrl} download="tts.mp3">
                <StandardButton
                  type="secondary"
                  variant="outlined"
                  size="small"
                >
                  {t('download_button')}
                </StandardButton>
              </a>
            </div>
          )}

          {loading && (
            <div className="text-center">
              <Spin tip={t('processing')} />
            </div>
          )}
        </Card>

        <Card className="p-6 rounded-xl shadow-lg mt-6">
          <div className="flex items-center justify-between mb-4">
            <div>
              <h3 className="text-base font-semibold text-gray-800 m-0">
                {t('tts_history_title')}
              </h3>
              <p className="text-xs text-gray-500 m-0 mt-1">
                {t('tts_history_subtitle')}
              </p>
            </div>
            {history.length > 0 && (
              <Popconfirm
                title={t('tts_history_clear_confirm')}
                okText={t('tts_history_clear')}
                cancelText={t('cancel')}
                onConfirm={handleClearHistory}
              >
                <StandardButton type="secondary" variant="outlined" size="small">
                  {t('tts_history_clear')}
                </StandardButton>
              </Popconfirm>
            )}
          </div>

          {history.length === 0 ? (
            <p className="text-sm text-gray-500 m-0">
              {t('tts_history_empty')}
            </p>
          ) : (
            <ul className="space-y-4 m-0 p-0 list-none">
              {history.map((entry) => (
                <li
                  key={entry.id}
                  className="border border-gray-200 rounded-lg p-4 bg-white"
                >
                  <div className="flex flex-col md:flex-row md:items-start md:justify-between gap-3">
                    <div className="min-w-0 flex-1">
                      <p className="text-sm text-gray-800 m-0 whitespace-pre-wrap break-words line-clamp-3">
                        {entry.text}
                      </p>
                      <p className="text-xs text-gray-500 mt-2 m-0">
                        {entry.voiceLabel}
                        {" · "}
                        {new Date(entry.createdAt).toLocaleString()}
                      </p>
                    </div>
                    <div className="flex flex-wrap items-center gap-2 shrink-0">
                      <StandardButton
                        type="secondary"
                        variant="outlined"
                        size="small"
                        onClick={() => handleReuse(entry)}
                      >
                        {t('tts_history_reuse')}
                      </StandardButton>
                      <a href={entry.audioDataUrl} download="tts.mp3">
                        <StandardButton
                          type="secondary"
                          variant="outlined"
                          size="small"
                        >
                          {t('download_button')}
                        </StandardButton>
                      </a>
                      <Popconfirm
                        title={t('tts_history_delete_confirm')}
                        okText={t('tts_history_delete')}
                        cancelText={t('cancel')}
                        onConfirm={() => handleDeleteEntry(entry.id)}
                      >
                        <StandardButton
                          type="secondary"
                          variant="outlined"
                          size="small"
                        >
                          {t('tts_history_delete')}
                        </StandardButton>
                      </Popconfirm>
                    </div>
                  </div>
                  <audio
                    controls
                    src={entry.audioDataUrl}
                    className="w-full mt-3"
                  />
                </li>
              ))}
            </ul>
          )}
        </Card>
      </div>
    </ProtectedToolWrapper>
  );
};

export default TextToSpeech;
